import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { respondToShowGroup, getShowsByGroup, get } from "../../services/api";
import { useUserRole } from "../../context/UserRoleContext";
import {
  formatCivilDateLong,
  normalizeBarTimeZone,
} from "../../utils/civilDateTime";

const ShowAcceptDeclineModal = ({
  isOpen,
  onClose,
  showId,
  recurringGroupId,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { userData } = useUserRole();
  const [shows, setShows] = useState([]);
  const [selectedShows, setSelectedShows] = useState(new Set());
  const [loadingShow, setLoadingShow] = useState(true);
  const [error, setError] = useState(null);
  const [fetchNotice, setFetchNotice] = useState(null);
  const [responding, setResponding] = useState(false);
  const [canRespond, setCanRespond] = useState(true);

  const calendarBasePath =
    userData?.role === "artist"
      ? "/artist"
      : userData?.role === "bar_owner"
        ? "/bar-owner"
        : "/bar-admin";

  // Fetch show details - either by group or single show
  useEffect(() => {
    const fetchShowDetails = async () => {
      if (!isOpen) {
        setLoadingShow(false);
        return;
      }

      try {
        setLoadingShow(true);
        setError(null);
        setFetchNotice(null);
        setShows([]);
        setSelectedShows(new Set());

        let response;

        // If recurringGroupId is provided, fetch all shows in the group
        if (recurringGroupId) {
          response = await getShowsByGroup(recurringGroupId);
        } else if (showId) {
          // Fallback: if showId is provided, try to get the group from the show
          const allShowsResponse = await get("/shows/me");
          if (allShowsResponse?.success && allShowsResponse.shows) {
            const showIdNum =
              typeof showId === "string" ? parseInt(showId, 10) : showId;
            const foundShow = allShowsResponse.shows.find((s) => {
              const sIdNum =
                typeof s.show_id === "string"
                  ? parseInt(s.show_id, 10)
                  : s.show_id;
              return (
                sIdNum === showIdNum ||
                s.show_id === showId ||
                String(s.show_id) === String(showId)
              );
            });

            if (foundShow && foundShow.recurring_group_id) {
              // Fetch all shows in the group
              response = await getShowsByGroup(foundShow.recurring_group_id);
            } else if (foundShow) {
              // Single show, create array with one show
              response = { success: true, shows: [foundShow] };
            } else {
              setFetchNotice({
                title: "Request not found",
                body: "This show may have been removed or is no longer available. If you already responded from your calendar, your choice is saved.",
              });
              setLoadingShow(false);
              return;
            }
          } else {
            setFetchNotice({
              title: "Could not load shows",
              body: "Something went wrong loading your shows. Try again from your calendar.",
            });
            setLoadingShow(false);
            return;
          }
        } else {
          setFetchNotice({
            title: "Missing request",
            body: "Open this request from your notifications or calendar.",
          });
          setLoadingShow(false);
          return;
        }

        if (!response || !response.success) {
          setFetchNotice({
            title: "Could not load request",
            body:
              response?.error ||
              "Something went wrong. You can try again from your calendar.",
          });
          setLoadingShow(false);
          return;
        }

        if (!response.shows || !Array.isArray(response.shows)) {
          setFetchNotice({
            title: "Could not load request",
            body: "Invalid response from the server. Try again from your calendar.",
          });
          setLoadingShow(false);
          return;
        }

        const pendingShows = response.shows.filter(
          (s) => s.status === "pending"
        );

        if (pendingShows.length === 0) {
          setFetchNotice({
            title: "Nothing to respond to",
            body: "You may have already accepted or declined these shows from your calendar, or this request is no longer pending.",
          });
          setLoadingShow(false);
          return;
        }

        setShows(pendingShows);
        setCanRespond(response.canRespond !== false);
        // Select all shows by default
        setSelectedShows(new Set(pendingShows.map((s) => s.show_id)));
      } catch (err) {
        console.error("Error fetching show details:", err);
        setFetchNotice({
          title: "Could not load request",
          body: err.message || "Failed to load show details.",
        });
      } finally {
        setLoadingShow(false);
      }
    };

    fetchShowDetails();
  }, [isOpen, showId, recurringGroupId]);

  const handleToggleShow = (showIdToToggle) => {
    setSelectedShows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(showIdToToggle)) {
        newSet.delete(showIdToToggle);
      } else {
        newSet.add(showIdToToggle);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedShows(new Set(shows.map((s) => s.show_id)));
  };

  const handleDeselectAll = () => {
    setSelectedShows(new Set());
  };

  const handleAcceptSelected = async () => {
    if (responding || selectedShows.size === 0) return;

    try {
      setResponding(true);
      setError(null);

      const showIdsToAccept = Array.from(selectedShows);
      const showIdsToReject = shows
        .filter((s) => !selectedShows.has(s.show_id))
        .map((s) => s.show_id);

      const result = await respondToShowGroup(
        showIdsToAccept,
        showIdsToReject,
        shows[0]?.recurring_group_id
      );

      if (result.success) {
        if (onSuccess) {
          // Call onSuccess and wait for it to complete before closing
          await onSuccess(result, "accept");
        }
        // Small delay to ensure state updates propagate
        setTimeout(() => {
          onClose();
        }, 200);
      } else {
        setError(result.error || "Failed to accept shows");
      }
    } catch (err) {
      setError(err.message || "Failed to accept shows");
    } finally {
      setResponding(false);
    }
  };

  const handleRejectAll = async () => {
    if (responding) return;

    try {
      setResponding(true);
      setError(null);

      const showIdsToReject = shows.map((s) => s.show_id);

      const result = await respondToShowGroup(
        [],
        showIdsToReject,
        shows[0]?.recurring_group_id
      );

      if (result.success) {
        if (onSuccess) {
          // Call onSuccess and wait for it to complete before closing
          await onSuccess(result, "reject");
        }
        // Small delay to ensure state updates propagate
        setTimeout(() => {
          onClose();
        }, 200);
      } else {
        setError(result.error || "Failed to reject shows");
      }
    } catch (err) {
      setError(err.message || "Failed to reject shows");
    } finally {
      setResponding(false);
    }
  };

  const formatDate = (dateString, barTimeZone) =>
    formatCivilDateLong(dateString, normalizeBarTimeZone(barTimeZone));

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes}${ampm}`;
  };

  if (!isOpen) {
    return null;
  }

  const firstShow = shows[0];
  const isRecurring = shows.length > 1;

  return (
    <div
      className="invite-response-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="invite-response-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="show-response-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div className="invite-response-modal-header">
          <h2 id="show-response-modal-title">
            {isRecurring ? "Recurring Show Requests" : "Show Request"}
          </h2>
          <button
            type="button"
            className="invite-response-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div
          className="invite-response-modal-body"
          style={{ flex: 1, overflow: "auto" }}
        >
          {loadingShow ? (
            <div className="invite-response-loading">
              <p>Loading show details...</p>
            </div>
          ) : fetchNotice && shows.length === 0 ? (
            <div className="notification-stale-panel">
              <h3 className="notification-stale-title">{fetchNotice.title}</h3>
              <p className="notification-stale-body">{fetchNotice.body}</p>
              <div className="notification-stale-actions">
                <button
                  type="button"
                  className="invite-response-close-btn"
                  onClick={onClose}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="invite-response-accept-btn"
                  onClick={() => {
                    navigate(`${calendarBasePath}/calendar`);
                    onClose();
                  }}
                >
                  View calendar
                </button>
              </div>
            </div>
          ) : shows.length > 0 ? (
            <>
              {error && (
                <div className="invite-response-error">
                  <p>{error}</p>
                </div>
              )}

              <div className="invite-response-content">
                {/* Consolidated header info */}
                <div className="invite-response-bars">
                  <h3>Show Details:</h3>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      marginBottom: "1rem",
                    }}
                  >
                    <li>
                      <strong>Venue:</strong> {firstShow.bar_name || "Unknown"}
                    </li>
                    {firstShow.bar_address && (
                      <li>
                        <strong>Address:</strong> {firstShow.bar_address}
                      </li>
                    )}
                    <li>
                      <strong>Time:</strong> {formatTime(firstShow.start_time)}{" "}
                      - {formatTime(firstShow.end_time)}
                    </li>
                    {firstShow.base_pay !== undefined && (
                      <li>
                        <strong>Base Pay:</strong> ${firstShow.base_pay} per
                        show
                      </li>
                    )}
                    {isRecurring && (
                      <li>
                        <strong>Total Shows:</strong> {shows.length}
                      </li>
                    )}
                  </ul>
                </div>

                {/* Scrollable list of dates with checkboxes (owner only) or read-only list */}
                {isRecurring && (
                  <div style={{ marginTop: "1.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <h3 style={{ margin: 0 }}>
                        {canRespond ? "Select Shows:" : "Shows:"}
                      </h3>
                      {canRespond && (
                      <div>
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--primary-red)",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            marginRight: "0.5rem",
                          }}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleDeselectAll}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--primary-red)",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                          }}
                        >
                          Deselect All
                        </button>
                      </div>
                      )}
                    </div>
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: "1px solid var(--border-gray)",
                        borderRadius: "8px",
                        padding: "0.5rem",
                      }}
                    >
                      {shows.map((show) => (
                        canRespond ? (
                        <label
                          key={show.show_id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "0.75rem",
                            cursor: "pointer",
                            borderRadius: "4px",
                            marginBottom: "0.25rem",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "var(--bg-gray-light)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedShows.has(show.show_id)}
                            onChange={() => handleToggleShow(show.show_id)}
                            style={{
                              marginRight: "0.75rem",
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                            }}
                          />
                          <span style={{ flex: 1 }}>
                            <strong>{formatDate(show.date, show.bar_time_zone)}</strong>
                          </span>
                        </label>
                        ) : (
                        <div
                          key={show.show_id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "0.75rem",
                            borderRadius: "4px",
                            marginBottom: "0.25rem",
                          }}
                        >
                          <span style={{ flex: 1 }}>
                            <strong>{formatDate(show.date, show.bar_time_zone)}</strong>
                          </span>
                        </div>
                        )
                      ))}
                    </div>
                    {canRespond && (
                    <p
                      style={{
                        marginTop: "0.75rem",
                        fontSize: "0.85rem",
                        color: "var(--text-gray)",
                      }}
                    >
                      {selectedShows.size} of {shows.length} show(s) selected
                    </p>
                    )}
                  </div>
                )}

                {/* Single show display */}
                {!isRecurring && (
                  <div style={{ marginTop: "1rem" }}>
                    <p>
                      <strong>Date:</strong>{" "}
                      {formatDate(firstShow.date, firstShow.bar_time_zone)}
                    </p>
                  </div>
                )}
              </div>

              <div className="invite-response-actions">
                {canRespond ? (
                  <>
                    <button
                      type="button"
                      className="invite-response-decline-btn"
                      onClick={handleRejectAll}
                      disabled={responding}
                    >
                      {responding ? "Processing..." : "Reject All"}
                    </button>
                    <button
                      type="button"
                      className="invite-response-accept-btn"
                      onClick={handleAcceptSelected}
                      disabled={responding || selectedShows.size === 0}
                    >
                      {responding
                        ? "Processing..."
                        : isRecurring
                        ? `Accept Selected (${selectedShows.size})`
                        : "Accept"}
                    </button>
                  </>
                ) : (
                  <>
                    <p
                      style={{
                        flex: 1,
                        margin: 0,
                        fontSize: "0.9rem",
                        color: "var(--text-gray)",
                      }}
                    >
                      Only the artist owner can accept or decline this request.
                    </p>
                    <button
                      type="button"
                      className="invite-response-close-btn"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="invite-response-error">
              <p>No shows found</p>
              <button
                type="button"
                className="invite-response-close-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowAcceptDeclineModal;
